import { Maybe } from "@figurepos/fgr-lib-business-logic/lib/common/types"
import { find, head, isNil, map, prop, reject, values } from "ramda"
import { pipeline } from "@figurepos/fgr-lib-business-logic/lib/common/functional"
import { formatPrice } from "../../../utils/formatters/priceFormatters"
import { Symbol } from "../../../constants"
import { formatUnitBasedPrice } from "../../../utils/formatters"
import {
    CategoriesListItem,
    CategoriesListItemOverride,
    type CategoriesListResponse,
    CategoryPricingType,
} from "../../../graphql/types"
import { max, min } from "lodash"
import { ItemIsActiveFilter, ItemsAndCategoriesTableFilters } from "./types"
import { CategoryImage, ImageUploadCropType } from "../../../events/Category/types"
import {
    CategoryAddRequestedPayload,
    CategoryUpdateRequestedPayload,
    ProductAddRequestedPayload,
    ProductUpdateRequestedPayload,
} from "../../../events/Category/types"

export const computeOverriddenValue = <T>(
    overrides: any[],
    locationId: Maybe<string>,
    id: string,
    selector: (arg0: any) => T
): Maybe<T> => {
    if (!locationId) {
        return null
    }

    return pipeline(
        overrides,
        find(o => o.locationId === locationId && o.data.id === id),
        prop("data"),
        selector
    )
}

export const formatOptionsPriceRange = (price: number[]): string => {
    if (price.length === 0) {
        return Symbol.MDash
    } else if (price.length === 1) {
        return formatPrice(price[0])
    } else {
        return `${formatPrice(price[0])}${Symbol.MDash}${formatPrice(price[1])}`
    }
}

export const formatModifiersPriceRange = (price: number[]): string => {
    if (price.length === 0) {
        return Symbol.MDash
    } else if (price.length === 1) {
        return formatPrice(price[0])
    } else {
        return `${formatPrice(Math.min(...price))}${Symbol.MDash}${formatPrice(Math.max(...price))}`
    }
}

export const formatCategoryPriceRange = (price: Maybe<number[]>, unit: Maybe<string>): Maybe<string> => {
    if (!price) {
        return null
    } else if (price.length === 0) {
        return "Set Upon Sale"
    } else if (price.length === 1 && unit) {
        return formatUnitBasedPrice(price[0], unit)
    } else if (price.length === 1) {
        return formatPrice(price[0])
    } else {
        return `${formatPrice(price[0])}${Symbol.MDash}${formatPrice(price[1])}`
    }
}

export const cateringTypes = {
    person: "person",
    serving: "serving",
    tray: "tray",
    platter: "platter",
    dozen: "dozen",
    halfTray: "halfTray",
    package: "package",
    pan: "pan",
    batch: "batch",
}

export const cateringTypeLabels = {
    person: "Person",
    serving: "Serving",
    tray: "Tray",
    platter: "Platter",
    dozen: "Dozen",
    halfTray: "1/2 Tray",
    package: "Package",
    pan: "Pan",
    batch: "Batch",
}

export const cateringTypesArray = values(cateringTypes)

export const isCateringType = (unit: string): boolean => cateringTypesArray.includes(unit)

export const getProductListItemPrice = (
    productToSave: ProductAddRequestedPayload | ProductUpdateRequestedPayload,
    originProductListItem: Maybe<CategoriesListItem>
): Maybe<number[]> => {
    if (productToSave.optionGroup) {
        const changedOptionsPrices = ((productToSave?.optionGroup?.options as any[]) ?? []).filter(
            val => val.price !== undefined
        )

        if (!changedOptionsPrices || changedOptionsPrices.length === 0) {
            return originProductListItem?.price
        }

        const optionsPrices: number[] = pipeline(
            productToSave?.optionGroup?.options ?? [],
            map(val => val.price),
            reject(isNil)
        ) as number[]

        if (!optionsPrices || optionsPrices.length === 0) {
            return [0]
        }

        return [min(optionsPrices) ?? 0, max(optionsPrices) ?? 0]
    }

    const price = "price" in productToSave ? productToSave.price : productToSave.fixedPrice

    if (price === undefined) {
        return originProductListItem?.price
    }

    if (price !== null && !isNaN(price)) {
        return [price]
    }

    return []
}

export const getProductListIsActive = (
    isOverride: boolean,
    productToSave: ProductAddRequestedPayload | ProductUpdateRequestedPayload,
    originProductListItem: Maybe<CategoriesListItem>
): boolean => {
    if (isOverride) {
        return false
    }

    if (productToSave?.isActive === undefined) {
        return originProductListItem?.isActive != null ? originProductListItem?.isActive : true
    }

    if (productToSave?.isActive !== null) {
        return productToSave?.isActive ?? true
    }

    return true
}

export const getProductListItemPriceForItemAdd = (productToAdd: ProductAddRequestedPayload): Maybe<number[]> => {
    if (productToAdd.optionGroup) {
        const optionsPrices: number[] = pipeline(
            productToAdd?.optionGroup?.options ?? [],
            map(val => val.price),
            reject(isNil)
        ) as number[]

        if (!optionsPrices || optionsPrices.length === 0) {
            return [0]
        }

        return [min(optionsPrices) ?? 0, max(optionsPrices) ?? 0]
    }

    if (productToAdd.price == null) {
        return null
    }

    return [productToAdd.price]
}

export const getProductPricingType = (
    productToSave: ProductAddRequestedPayload | ProductUpdateRequestedPayload,
    originProductListItem: Maybe<CategoriesListItem>
): Maybe<CategoryPricingType> => {
    const price = "price" in productToSave ? productToSave.price : productToSave.fixedPrice
    if (price === undefined && originProductListItem) {
        return originProductListItem.pricingType
    }

    if (price && productToSave.unit) {
        return CategoryPricingType.UnitBased
    }

    if (price || price === 0) {
        return CategoryPricingType.Fixed
    }

    if (productToSave.optionGroup) {
        return CategoryPricingType.SetInOptions
    }

    return CategoryPricingType.SetUponSale
}

export const getProductPricingTypeForProductAdd = (
    productToSave: ProductAddRequestedPayload
): Maybe<CategoryPricingType> => {
    if (productToSave.unit) {
        return CategoryPricingType.UnitBased
    }

    if (productToSave.price || productToSave.price === 0) {
        return CategoryPricingType.Fixed
    }

    if (productToSave.optionGroup) {
        return CategoryPricingType.SetInOptions
    }

    return CategoryPricingType.SetUponSale
}

export const getTableIsActiveFilter = (filters: ItemsAndCategoriesTableFilters): ItemIsActiveFilter[] => {
    switch (filters.isActive) {
        case ItemIsActiveFilter.Active:
            return [ItemIsActiveFilter.Active]
        case ItemIsActiveFilter.Inactive:
            return [ItemIsActiveFilter.Inactive]
        case ItemIsActiveFilter.All:
            return [ItemIsActiveFilter.Active, ItemIsActiveFilter.Inactive]
        default:
            return []
    }
}

export const getIsActiveFilterFromTableFilter = (isActive: ItemIsActiveFilter[]): ItemIsActiveFilter => {
    if (isActive.length === 2 || isActive.length === 0) {
        return ItemIsActiveFilter.All
    }
    if (isActive.length === 1) {
        return head(isActive) as ItemIsActiveFilter
    }
    return ItemIsActiveFilter.All
}

export const getAntTableIsActiveFilter = (filters: ItemsAndCategoriesTableFilters): boolean[] => {
    switch (filters.isActive) {
        case ItemIsActiveFilter.Active:
            return [true]
        case ItemIsActiveFilter.Inactive:
            return [false]
        case ItemIsActiveFilter.All:
            return []
        default:
            return []
    }
}

export const getIsActiveFilterFromAntTableFilter = (isActive: boolean[]): ItemIsActiveFilter => {
    if (isActive.length === 2 || isActive.length === 0) {
        return ItemIsActiveFilter.All
    }
    if (isActive.length === 1) {
        return isActive[0] ? ItemIsActiveFilter.Active : ItemIsActiveFilter.Inactive
    }
    return ItemIsActiveFilter.All
}

export const applyFiltersToTableData = <T extends { name: string; isActive: boolean }>(
    data: T[],
    searchValue: Maybe<string>,
    filters: ItemsAndCategoriesTableFilters
): T[] =>
    data.filter(item => {
        const searchMatches = searchValue ? item.name.toLowerCase().includes(searchValue.toLowerCase()) : true
        const activeMatches =
            (filters.isActive === ItemIsActiveFilter.Active && item.isActive === true) ||
            (filters.isActive === ItemIsActiveFilter.Inactive && item.isActive === false) ||
            filters.isActive === ItemIsActiveFilter.All

        return searchMatches && activeMatches
    })

export const getIsActiveLocationsOverrides = (
    itemsListData: Maybe<CategoriesListResponse>,
    itemToSave:
        | CategoryAddRequestedPayload
        | CategoryUpdateRequestedPayload
        | ProductAddRequestedPayload
        | ProductUpdateRequestedPayload,
    isCreating: boolean
): CategoriesListItemOverride[] => {
    if (!itemsListData) {
        return []
    }

    const categoryId = "categoryId" in itemToSave ? itemToSave.categoryId : itemToSave.productId

    // When adding new product/category,
    // add 'isActive' overrides according to 'isActiveLocations' array
    if (isCreating) {
        return [
            ...(itemsListData?.overrides ?? []),
            ...(
                itemToSave.isActiveLocations?.map(l => ({
                    locationId: l.id,
                    data: {
                        id: categoryId,
                        isActive: l.isActive,
                    },
                })) ?? []
            ).filter(Boolean),
        ]
    }

    // When updating existing product/category, we need to create/update/delete the overrides
    // according to 'isActiveLocations' array
    return (itemToSave.isActiveLocations ?? []).reduce((acc: CategoriesListItemOverride[], l) => {
        const existingOverride = itemsListData?.overrides?.find(o => o.locationId === l.id)

        // If there is no existing override, we need to add it
        if (!existingOverride) {
            return [
                ...acc,
                {
                    locationId: l.id,
                    data: {
                        id: categoryId,
                        isActive: l.isActive,
                    },
                },
            ]
        }

        // If there is an existing override, we need to update it
        if (existingOverride) {
            return acc.map(o => {
                if (o.data.id === categoryId && o.locationId === l.id) {
                    return {
                        ...o,
                        data: {
                            ...o.data,
                            isActive: l.isActive,
                        },
                    }
                }
                return o
            })
        }

        return acc
    }, itemsListData?.overrides ?? []) as CategoriesListItemOverride[]
}

export const pruneRowSelection = (
    selection: Record<string, boolean>,
    existingIds: Set<string>
): Record<string, boolean> => Object.fromEntries(Object.entries(selection).filter(([id]) => existingIds.has(id)))

export const getTileImagePathFromImages = (images: Maybe<CategoryImage[]>): Maybe<string> =>
    images?.[0]?.variants?.find(v => v.croppedType === ImageUploadCropType.Ratio1_1)?.url ?? null
